/**
 * pos-dd-iv L4.3 — leitura dos eventos de funil (`evento_funil`, contrato K2).
 *
 * Par do backend `GET /api/v1/funil/resumo`. O nome do arquivo é
 * `funilEventos` e não `funil` de propósito: `src/api/funil.ts` já existe e é
 * outra coisa — o funil PÚBLICO de aquisição (signup, planos, assinar, R6a).
 * Dois arquivos chamados `funil` na mesma casa seria a ambiguidade que faz
 * alguém importar o errado e só descobrir em produção.
 *
 * Escopo: quem chega como staff recebe o funil da PLATAFORMA; quem chega como
 * tenant recebe o próprio ciclo de vida. Não é o frontend que decide isso — é
 * a policy RLS sobre a sessão do request. O campo `escopo` só conta qual das
 * duas visões voltou, para a tela não apresentar um número de tenant como se
 * fosse o número da casa.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/services/api';

export interface ContagemDeEvento {
  /** Quantas LINHAS do evento no período. */
  linhas: number;
  /** Quantos CLIENTES DISTINTOS — `signup_iniciado` não tem dono, logo 0. */
  clientes: number;
}

export interface FunilResumo {
  desde: string;
  ate: string;
  /** Cadastros iniciados (linhas: a entrada não tem dono para agrupar). */
  entraram: number;
  /** Clientes distintos que tiveram pagamento confirmado. */
  converteram: number;
  /** Clientes distintos que deram churn. */
  sairam: number;
  /** `null` quando ninguém entrou — 0/0 é pergunta sem resposta, não zero. */
  taxa_conversao: number | null;
  por_evento: Record<string, ContagemDeEvento>;
  escopo: 'casa' | 'tenant';
}

/** Janelas oferecidas na tela. O backend recusa acima de 366 dias. */
export const JANELAS = [30, 90, 365] as const;
export type Janela = (typeof JANELAS)[number];

function diasAtras(dias: number): { desde: string; ate: string } {
  const hoje = new Date();
  // Fim EXCLUSIVO: amanhã, para o dia de hoje entrar inteiro. `ate` = hoje
  // devolveria o funil até a meia-noite passada, e "o número de hoje está
  // zerado" é a reclamação que essa escolha produz.
  const ate = new Date(hoje.getTime() + 24 * 60 * 60 * 1000);
  const desde = new Date(ate.getTime() - dias * 24 * 60 * 60 * 1000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { desde: iso(desde), ate: iso(ate) };
}

export function useFunilResumo(dias: Janela = 30) {
  const { desde, ate } = diasAtras(dias);
  return useQuery({
    queryKey: ['funil', 'resumo', desde, ate],
    queryFn: () => api.get<FunilResumo>(`/funil/resumo?desde=${desde}&ate=${ate}`),
    retry: false,
    staleTime: 60_000,
  });
}
